import { PAYPAL_CHECKOUT_TOKEN_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import logger from '@redbubble/next/logger';
import { getHost } from '@redbubble/utils/getHost';
import btWebClient from 'braintree-web/client';
import btWebDataCollector from 'braintree-web/data-collector';
import btWebPaypalCheckout from 'braintree-web/paypal-checkout';
import { useCallback, useRef, useState } from 'react';
import { ButtonsRenderer, ErrorMessage, PayPalCheckout } from './types';
import { getMerchantAccountId, getPaypalLocale } from './utils';

export interface UseInitPayPalProps {
  currency: string;
  locale: string;
}

export interface InitResult {
  initialising: boolean;
  clientDeviceData: string;
  paypalClient: PayPalCheckout | null;
  paypalButton: ButtonsRenderer | null;
  error: ErrorMessage | null;
}

export interface BraintreeTokenSuccessPayload {
  data: {
    token: string;
  };
}

export interface BraintreeTokenErrorPayload {
  errors: {
    code: string;
    title: string;
  }[];
}

export type BraintreeTokenPayload =
  | BraintreeTokenSuccessPayload
  | BraintreeTokenErrorPayload;

class BraintreeTokenError extends Error {
  constructor(payload: BraintreeTokenErrorPayload) {
    const message = payload.errors
      .map((err) => `${err.code}: ${err.title}`)
      .join(' | ');
    super(message);
    this.name = '';
  }
}

const tokenUrl = [getHost(), PAYPAL_CHECKOUT_TOKEN_PUBLIC_ENDPOINT].join('');

export const useInitPayPal = ({ currency, locale }: UseInitPayPalProps) => {
  const [result, setResult] = useState<InitResult>({
    initialising: false,
    clientDeviceData: '',
    paypalClient: null,
    paypalButton: null,
    error: null,
  });

  // Tracks initialisation state so that doInit() and
  // doReset() can work together in perfect harmony.
  const loaded = useRef(false);

  const doInit = useCallback(async () => {
    if (loaded.current) return null;

    setResult({
      initialising: true,
      clientDeviceData: '',
      paypalClient: null,
      paypalButton: null,
      error: null,
    });

    try {
      const btTokenRes = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const btTokenJson: BraintreeTokenPayload = await btTokenRes.json();

      if (!btTokenRes.ok) {
        if ('errors' in btTokenJson) throw new BraintreeTokenError(btTokenJson);

        throw new Error(
          'Unrecognizable API response format  when failed to fetch Braintree token'
        );
      }

      if (!('data' in btTokenJson)) {
        throw new Error('Unrecognizable API response format');
      }

      const { data } = btTokenJson;

      const btClient = await btWebClient.create({ authorization: data.token });
      const btCollector = await btWebDataCollector.create({ client: btClient });
      const clientDeviceData = btCollector.deviceData;

      // note we are coercing this into our own type (see types.ts) but it's not our fault!
      const paypalClient = (await btWebPaypalCheckout.create({
        client: btClient,
        merchantAccountId: getMerchantAccountId(currency),
      })) as PayPalCheckout;

      // once the sdk is loaded, a 'paypal' object will be attached to the 'window'
      await paypalClient.loadPayPalSDK({
        intent: 'capture',
        locale: getPaypalLocale(locale),
        currency,
        // according to the docs, the payment buttons automatically render all eligible buttons.
        // https://developer.paypal.com/sdk/js/reference/#link-fundingeligibility
        components: 'buttons',
        commit: true,
      });

      loaded.current = true;
      const res: InitResult = {
        initialising: false,
        clientDeviceData,
        paypalClient,
        paypalButton: window.paypal.Buttons,
        error: null,
      };
      setResult(res);
      return res;
    } catch (error) {
      const message = 'Failed to initalize PayPal';
      logger.error(
        {
          err: error,
          context: 'PayPalExpress/useInitPayPal',
          message,
        },
        message
      );

      const res: InitResult = {
        initialising: false,
        clientDeviceData: '',
        paypalClient: null,
        paypalButton: null,
        error: error.toString(),
      };
      setResult(res);
      return res;
    }
  }, [currency, locale]);

  const doReset = useCallback(async () => {
    return new Promise((resolve) => {
      if (!result.paypalClient) {
        resolve(null);
        return;
      }

      result.paypalClient.teardown(() => {
        loaded.current = false;
        setResult({
          initialising: false,
          clientDeviceData: '',
          paypalClient: null,
          paypalButton: null,
          error: null,
        });
        resolve(null);
      });
    });
  }, [result.paypalClient]);

  return {
    ...result,
    doInit,
    doReset,
  };
};
